import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchAzureLast3MonthsAnalysis,
  fetchAzureSubscriptionCost,
} from './kpiDashboardApi';
import { msrReportEnabled } from './useMsrReport';

const STALE_MS = 60_000;
const GC_MS = 10 * 60_000;

/** Derive Azure Cost Management calendar month from KPI export date range end. */
export function azureCostMonthYear(dateTo: string): { month: number; year: number } {
  if (dateTo?.trim()) {
    const d = new Date(`${dateTo.trim()}T12:00:00`);
    if (!Number.isNaN(d.getTime())) {
      return { month: d.getUTCMonth() + 1, year: d.getUTCFullYear() };
    }
  }
  const now = new Date();
  return { month: now.getUTCMonth() + 1, year: now.getUTCFullYear() };
}

export function useAzureCostSections(
  filterKey: string,
  filterValues: Record<string, string | string[]>,
  dateTo: string,
  options: { enabled: boolean }
) {
  const customerSelected = msrReportEnabled(filterValues);
  const customerId = (filterValues.customer as string) || '';
  const { month, year } = useMemo(() => azureCostMonthYear(dateTo), [dateTo]);
  const enabled = options.enabled && customerSelected;

  const subscription = useQuery({
    queryKey: ['kpi-azure-cost-subscription', filterKey, customerId, month, year],
    queryFn: () => fetchAzureSubscriptionCost(customerId, month, year),
    enabled,
    staleTime: STALE_MS,
    gcTime: GC_MS,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const last3MonthsAnalysis = useQuery({
    queryKey: ['kpi-azure-cost-last-3-months-analysis', filterKey, customerId, month, year],
    queryFn: () => fetchAzureLast3MonthsAnalysis(customerId, month, year),
    enabled,
    staleTime: STALE_MS,
    gcTime: GC_MS,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const refetchAll = () =>
    Promise.all([subscription.refetch(), last3MonthsAnalysis.refetch()]);

  return {
    subscription,
    last3MonthsAnalysis,
    refetchAll,
    month,
    year,
    customerId,
  };
}
